import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { 
    ArrowLeft, ArrowRight, Loader2, Download, Send, CheckCircle,
    Target, Star, Zap, ChevronRight, RefreshCw, Calendar, Lock,
    TrendingUp, Activity, Award
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { SENDAFooter } from '../components/SENDAFooter';
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine } from 'recharts';
import { formatDate } from '../lib/dateUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STEPS = ['intro', 'rating', 'priority', 'projects', 'generating', 'report', 'tracking'];

export default function PlanSenda3x3() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState(null);
    const [step, setStep] = useState('intro');
    const [domains, setDomains] = useState([]);
    const [domainScores, setDomainScores] = useState({});
    const [priorityDomains, setPriorityDomains] = useState([]);
    const [priorityDetails, setPriorityDetails] = useState({});
    const [projects, setProjects] = useState([
        { name: '', domain: '', why: '', blocker: '', if_postponed: '', if_action: '' },
        { name: '', domain: '', why: '', blocker: '', if_postponed: '', if_action: '' },
        { name: '', domain: '', why: '', blocker: '', if_postponed: '', if_action: '' }
    ]);
    const [plan, setPlan] = useState(null);
    const [generating, setGenerating] = useState(false);
    const [checkins, setCheckins] = useState([]);
    const [checkinForm, setCheckinForm] = useState({ completed: '', avoided: '', emotion: '', pattern_learning: '', next_week_action: '' });
    const [executionScore, setExecutionScore] = useState(null);
    const [versionComparison, setVersionComparison] = useState(null);
    const [scoreHistory, setScoreHistory] = useState([]);
    const [showEvolution, setShowEvolution] = useState(false);

    const fetchStatus = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API_URL}/api/user/plan3x3/status`, { withCredentials: true });
            setStatus(data);
            setDomains(data.domains || []);
            
            if (data.plan_exists) {
                const planRes = await axios.get(`${API_URL}/api/user/plan3x3`, { withCredentials: true });
                if (planRes.data.plan) {
                    setPlan(planRes.data.plan);
                    setDomainScores(planRes.data.plan.domain_scores || {});
                    setPriorityDomains(planRes.data.plan.priority_domains || []);
                    setProjects(planRes.data.plan.projects || projects);
                    if (['approved', 'delivered'].includes(planRes.data.plan.state)) {
                        setStep('report');
                        const checkinsRes = await axios.get(`${API_URL}/api/user/plan3x3/checkins`, { withCredentials: true });
                        setCheckins(checkinsRes.data.checkins || []);
                        const scoreRes = await axios.get(`${API_URL}/api/user/plan3x3/execution-score`, { withCredentials: true });
                        setExecutionScore(scoreRes.data.score_data);
                        const historyRes = await axios.get(`${API_URL}/api/user/plan3x3/score-history`, { withCredentials: true });
                        setScoreHistory(historyRes.data.history || []);
                    } else if (planRes.data.plan.state === 'under_review') {
                        setStep('report');
                    } else {
                        setStep('report');
                        // Fetch version comparison if exists
                        try {
                            const compRes = await axios.get(`${API_URL}/api/user/plan3x3/version-comparison`, { withCredentials: true });
                            setVersionComparison(compRes.data.comparison);
                        } catch { /* silent */ }
                    }
                }
            } else {
                // Load draft if exists
                const draftRes = await axios.get(`${API_URL}/api/user/plan3x3/draft`, { withCredentials: true });
                if (draftRes.data.data) {
                    const d = draftRes.data.data;
                    if (d.domainScores) setDomainScores(d.domainScores);
                    if (d.priorityDomains) setPriorityDomains(d.priorityDomains);
                    if (d.priorityDetails) setPriorityDetails(d.priorityDetails);
                    if (d.projects) setProjects(d.projects);
                }
            }
        } catch {
            toast.error('Error al cargar estado del plan');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchStatus(); }, [fetchStatus]);

    const saveDraft = useCallback(async () => {
        try {
            await axios.post(`${API_URL}/api/user/plan3x3/save-input`, { domainScores, priorityDomains, priorityDetails, projects }, { withCredentials: true });
        } catch { /* silent */ }
    }, [domainScores, priorityDomains, priorityDetails, projects]);

    const handleGenerate = async () => {
        setStep('generating');
        setGenerating(true);
        try {
            await saveDraft();
            const { data } = await axios.post(`${API_URL}/api/user/plan3x3/generate`, {
                domain_scores: domainScores,
                priority_domains: priorityDomains.map(id => {
                    const dom = domains.find(d => d.id === id);
                    return { id, name: dom?.name || id, ...priorityDetails[id] };
                }),
                projects
            }, { withCredentials: true });
            setPlan(data.plan);
            setStep('report');
            toast.success('Plan SENDA 3x3 generado exitosamente');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Error al generar el plan');
            setStep('projects');
        } finally {
            setGenerating(false);
        }
    };

    const handleSubmitForReview = async () => {
        try {
            await axios.post(`${API_URL}/api/user/plan3x3/submit-for-review`, {}, { withCredentials: true });
            setPlan(prev => ({ ...prev, state: 'under_review' }));
            toast.success('Plan enviado a revisión de su mentora');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Error al enviar');
        }
    };

    const handleCheckin = async () => {
        try {
            await axios.post(`${API_URL}/api/user/plan3x3/checkin`, checkinForm, { withCredentials: true });
            toast.success('Seguimiento semanal registrado');
            setCheckinForm({ completed: '', avoided: '', emotion: '', pattern_learning: '', next_week_action: '' });
            const res = await axios.get(`${API_URL}/api/user/plan3x3/checkins`, { withCredentials: true });
            setCheckins(res.data.checkins || []);
            // Refresh execution score
            const scoreRes = await axios.get(`${API_URL}/api/user/plan3x3/execution-score`, { withCredentials: true });
            setExecutionScore(scoreRes.data.score_data);
            const historyRes = await axios.get(`${API_URL}/api/user/plan3x3/score-history`, { withCredentials: true });
            setScoreHistory(historyRes.data.history || []);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Error al registrar');
        }
    };

    const handleRegenerate = () => {
        setPlan(null);
        setStep('rating');
    };

    const handleDownloadPDF = async () => {
        try {
            const response = await axios.get(`${API_URL}/api/user/plan3x3/pdf`, { withCredentials: true, responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.download = 'Plan_SENDA_3x3.pdf';
            link.click();
        } catch {
            toast.error('Error al descargar PDF');
        }
    };

    if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#FBFBF9]"><Loader2 className="h-8 w-8 animate-spin text-[#3E5F6E]" /></div>;

    if (!status?.eligible) {
        return (
            <div className="min-h-screen bg-[#FBFBF9] flex items-center justify-center p-4">
                <Card className="max-w-md w-full bg-white border-[#EAE8E3]">
                    <CardContent className="pt-8 text-center space-y-4">
                        <Lock className="h-12 w-12 text-[#9AA5B1] mx-auto" />
                        <h2 className="text-lg font-semibold text-[#1F2933] font-['Outfit']">Plan SENDA 3x3</h2>
                        <p className="text-sm text-[#616E7C]">Este módulo se desbloquea al completar los 9 capítulos del método SENDA y obtener su certificado.</p>
                        <div className="space-y-2 text-left">
                            <div className="flex items-center gap-2 text-sm">
                                {status?.modules_complete ? <CheckCircle className="h-4 w-4 text-[#5FAF9D]" /> : <Lock className="h-4 w-4 text-[#9AA5B1]" />}
                                <span className={status?.modules_complete ? 'text-[#5FAF9D]' : 'text-[#9AA5B1]'}>9 módulos completados</span>
                            </div>
                            <div className="flex items-center gap-2 text-sm">
                                {status?.has_certificate ? <CheckCircle className="h-4 w-4 text-[#5FAF9D]" /> : <Lock className="h-4 w-4 text-[#9AA5B1]" />}
                                <span className={status?.has_certificate ? 'text-[#5FAF9D]' : 'text-[#9AA5B1]'}>Certificado SENDA obtenido</span>
                            </div>
                        </div>
                        <Button variant="outline" onClick={() => navigate('/usuario')} className="mt-4">Volver al dashboard</Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    // INTRO STEP
    if (step === 'intro') {
        return (
            <div className="min-h-screen bg-[#FBFBF9]">
                <header className="bg-white border-b border-[#EAE8E3] px-4 py-3 sticky top-0 z-10">
                    <div className="max-w-3xl mx-auto flex items-center gap-3">
                        <Button variant="ghost" size="sm" onClick={() => navigate('/usuario')} data-testid="back-btn"><ArrowLeft className="h-4 w-4" /></Button>
                        <h1 className="text-lg font-semibold text-[#1F2933] font-['Outfit']">Plan SENDA 3x3</h1>
                    </div>
                </header>
                <main className="max-w-3xl mx-auto p-4 sm:p-6">
                    <Card className="bg-white border-[#EAE8E3] shadow-sm overflow-hidden">
                        <div className="bg-gradient-to-br from-[#3E5F6E] to-[#2F4A57] p-8 text-center">
                            <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                <Target className="h-8 w-8 text-white" />
                            </div>
                            <h2 className="text-2xl font-bold text-white font-['Outfit'] mb-2">Plan SENDA 3x3</h2>
                            <p className="text-white/70 text-sm">Deje de improvisar. Comience a ejecutar con dirección.</p>
                        </div>
                        <CardContent className="p-6 sm:p-8 space-y-6">
                            <p className="text-sm text-[#323F4B] leading-relaxed">
                                Este sistema transforma su patrón de procrastinación en un camino de ejecución claro. Usted priorizará 3 áreas clave de su vida, definirá 3 proyectos significativos y recibirá 3 acciones concretas por proyecto para comenzar a avanzar con claridad.
                            </p>
                            <div className="bg-[#FBFBF9] rounded-xl p-4 border border-[#EAE8E3]">
                                <p className="text-xs font-semibold text-[#425A65] uppercase mb-3">Su plan incluye:</p>
                                <ul className="space-y-2 text-sm text-[#616E7C]">
                                    <li className="flex items-start gap-2"><Zap className="h-4 w-4 text-[#5FAF9D] mt-0.5 shrink-0" />Diagnóstico estratégico personalizado</li>
                                    <li className="flex items-start gap-2"><Zap className="h-4 w-4 text-[#5FAF9D] mt-0.5 shrink-0" />Mapa de bloqueo basado en sus datos SENDA</li>
                                    <li className="flex items-start gap-2"><Zap className="h-4 w-4 text-[#5FAF9D] mt-0.5 shrink-0" />Plan de ejecución 3x3 (3 proyectos × 3 acciones)</li>
                                    <li className="flex items-start gap-2"><Zap className="h-4 w-4 text-[#5FAF9D] mt-0.5 shrink-0" />Protocolo diario SENDA</li>
                                    <li className="flex items-start gap-2"><Zap className="h-4 w-4 text-[#5FAF9D] mt-0.5 shrink-0" />Regla personal de ejecución</li>
                                </ul>
                            </div>
                            <Button className="w-full bg-[#3E5F6E] hover:bg-[#2F4A57] text-white h-12 text-base" onClick={() => setStep('rating')} data-testid="start-plan-btn">
                                Generar Mi Plan SENDA 3x3 <ChevronRight className="ml-2 h-5 w-5" />
                            </Button>
                        </CardContent>
                    </Card>
                </main>
                <SENDAFooter />
            </div>
        );
    }

    // DOMAIN RATING STEP
    if (step === 'rating') {
        const allRated = domains.every(d => domainScores[d.id] >= 1);
        return (
            <div className="min-h-screen bg-[#FBFBF9]">
                <header className="bg-white border-b border-[#EAE8E3] px-4 py-3 sticky top-0 z-10">
                    <div className="max-w-3xl mx-auto flex items-center gap-3">
                        <Button variant="ghost" size="sm" onClick={() => setStep('intro')}><ArrowLeft className="h-4 w-4" /></Button>
                        <div className="flex-1"><h1 className="text-lg font-semibold text-[#1F2933] font-['Outfit']">Dominios de Vida</h1><p className="text-xs text-[#9AA5B1]">Paso 1 de 3</p></div>
                    </div>
                </header>
                <main className="max-w-3xl mx-auto p-4 sm:p-6 space-y-4">
                    <p className="text-sm text-[#616E7C]">Puntúe cada dominio de su vida del 1 al 10, donde 1 es insatisfecho y 10 es plenamente satisfecho.</p>
                    {domains.map(domain => (
                        <Card key={domain.id} className="bg-white border-[#EAE8E3]">
                            <CardContent className="p-4">
                                <div className="flex items-start justify-between gap-4">
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-[#1F2933]">{domain.name}</p>
                                        <p className="text-xs text-[#9AA5B1] mt-0.5">{domain.description}</p>
                                    </div>
                                    <div className="flex items-center gap-1 shrink-0">
                                        {[1,2,3,4,5,6,7,8,9,10].map(n => (
                                            <button
                                                key={n}
                                                onClick={() => setDomainScores(prev => ({...prev, [domain.id]: n}))}
                                                className={`w-7 h-7 rounded-lg text-xs font-medium transition-all ${
                                                    domainScores[domain.id] === n 
                                                        ? 'bg-[#3E5F6E] text-white shadow-sm' 
                                                        : domainScores[domain.id] > n 
                                                            ? 'bg-[#3E5F6E]/10 text-[#3E5F6E]'
                                                            : 'bg-[#F5F4F1] text-[#9AA5B1] hover:bg-[#EAE8E3]'
                                                }`}
                                                data-testid={`score-${domain.id}-${n}`}
                                            >{n}</button>
                                        ))}
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                    <Button className="w-full bg-[#3E5F6E] hover:bg-[#2F4A57] text-white h-11" disabled={!allRated} onClick={() => { saveDraft(); setStep('priority'); }} data-testid="next-priority-btn">
                        Continuar <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                </main>
            </div>
        );
    }

    // PRIORITY SELECTION STEP
    if (step === 'priority') {
        const togglePriority = (id) => {
            setPriorityDomains(prev => {
                if (prev.includes(id)) return prev.filter(x => x !== id);
                if (prev.length >= 3) return prev;
                return [...prev, id];
            });
        };
        const canContinue = priorityDomains.length === 3 && priorityDomains.every(id => priorityDetails[id]?.current && priorityDetails[id]?.desired && priorityDetails[id]?.why);

        return (
            <div className="min-h-screen bg-[#FBFBF9]">
                <header className="bg-white border-b border-[#EAE8E3] px-4 py-3 sticky top-0 z-10">
                    <div className="max-w-3xl mx-auto flex items-center gap-3">
                        <Button variant="ghost" size="sm" onClick={() => setStep('rating')}><ArrowLeft className="h-4 w-4" /></Button>
                        <div className="flex-1"><h1 className="text-lg font-semibold text-[#1F2933] font-['Outfit']">Dominios Prioritarios</h1><p className="text-xs text-[#9AA5B1]">Paso 2 de 3</p></div>
                        <Badge className="bg-[#3E5F6E] text-white">{priorityDomains.length}/3</Badge>
                    </div>
                </header>
                <main className="max-w-3xl mx-auto p-4 sm:p-6 space-y-4">
                    <p className="text-sm text-[#616E7C]">Seleccione los 3 dominios donde la procrastinación ha retrasado un progreso significativo.</p>
                    <div className="grid gap-2">
                        {domains.sort((a, b) => (domainScores[a.id] || 0) - (domainScores[b.id] || 0)).map(domain => {
                            const isSelected = priorityDomains.includes(domain.id);
                            return (
                                <div key={domain.id} className={`p-3 rounded-xl border cursor-pointer transition-all ${isSelected ? 'border-[#3E5F6E] bg-[#3E5F6E]/5' : 'border-[#EAE8E3] bg-white hover:border-[#9AA5B1]'}`} onClick={() => togglePriority(domain.id)} data-testid={`priority-${domain.id}`}>
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm font-medium text-[#1F2933]">{domain.name}</span>
                                        <div className="flex items-center gap-2">
                                            <Badge variant="outline" className="text-xs">{domainScores[domain.id]}/10</Badge>
                                            {isSelected && <CheckCircle className="h-4 w-4 text-[#3E5F6E]" />}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    {priorityDomains.length === 3 && (
                        <div className="space-y-4 mt-6">
                            <p className="text-sm font-medium text-[#425A65]">Detalle para cada dominio prioritario:</p>
                            {priorityDomains.map(id => {
                                const dom = domains.find(d => d.id === id);
                                const detail = priorityDetails[id] || {};
                                return (
                                    <Card key={id} className="bg-white border-[#EAE8E3]">
                                        <CardContent className="p-4 space-y-3">
                                            <p className="text-sm font-semibold text-[#3E5F6E]">{dom?.name}</p>
                                            <div className="grid grid-cols-2 gap-3">
                                                <div>
                                                    <label className="text-xs text-[#9AA5B1]">Nivel actual</label>
                                                    <Input type="number" min="1" max="10" value={detail.current || ''} onChange={e => setPriorityDetails(prev => ({...prev, [id]: {...(prev[id]||{}), current: e.target.value}}))} className="mt-1" data-testid={`current-${id}`} />
                                                </div>
                                                <div>
                                                    <label className="text-xs text-[#9AA5B1]">Nivel deseado</label>
                                                    <Input type="number" min="1" max="10" value={detail.desired || ''} onChange={e => setPriorityDetails(prev => ({...prev, [id]: {...(prev[id]||{}), desired: e.target.value}}))} className="mt-1" data-testid={`desired-${id}`} />
                                                </div>
                                            </div>
                                            <div>
                                                <label className="text-xs text-[#9AA5B1]">¿Por qué esta área importa ahora?</label>
                                                <Textarea value={detail.why || ''} onChange={e => setPriorityDetails(prev => ({...prev, [id]: {...(prev[id]||{}), why: e.target.value}}))} className="mt-1" rows={2} data-testid={`why-${id}`} />
                                            </div>
                                        </CardContent>
                                    </Card>
                                );
                            })}
                        </div>
                    )}
                    <Button className="w-full bg-[#3E5F6E] hover:bg-[#2F4A57] text-white h-11" disabled={!canContinue} onClick={() => { saveDraft(); setStep('projects'); }} data-testid="next-projects-btn">
                        Continuar <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                </main>
            </div>
        );
    }

    // PROJECTS STEP
    if (step === 'projects') {
        const updateProject = (idx, field, value) => {
            setProjects(prev => prev.map((p, i) => i === idx ? {...p, [field]: value} : p));
        };
        const canGenerate = projects.every(p => p.name.trim() && p.domain && p.why.trim() && p.blocker.trim());

        return (
            <div className="min-h-screen bg-[#FBFBF9]">
                <header className="bg-white border-b border-[#EAE8E3] px-4 py-3 sticky top-0 z-10">
                    <div className="max-w-3xl mx-auto flex items-center gap-3">
                        <Button variant="ghost" size="sm" onClick={() => setStep('priority')}><ArrowLeft className="h-4 w-4" /></Button>
                        <div className="flex-1"><h1 className="text-lg font-semibold text-[#1F2933] font-['Outfit']">3 Proyectos Personales</h1><p className="text-xs text-[#9AA5B1]">Paso 3 de 3</p></div>
                    </div>
                </header>
                <main className="max-w-3xl mx-auto p-4 sm:p-6 space-y-6">
                    <p className="text-sm text-[#616E7C]">Defina 3 proyectos personales que han sido afectados por la procrastinación.</p>
                    {projects.map((project, idx) => (
                        <Card key={idx} className="bg-white border-[#EAE8E3]">
                            <CardHeader className="pb-2">
                                <CardTitle className="text-sm text-[#3E5F6E] flex items-center gap-2">
                                    <span className="w-6 h-6 rounded-full bg-[#3E5F6E] text-white text-xs flex items-center justify-center">{idx + 1}</span>
                                    Proyecto {idx + 1}
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-3">
                                <div>
                                    <label className="text-xs text-[#9AA5B1]">Nombre del proyecto</label>
                                    <Input value={project.name} onChange={e => updateProject(idx, 'name', e.target.value)} className="mt-1" placeholder="Ej: Lanzar mi negocio online" data-testid={`project-name-${idx}`} />
                                </div>
                                <div>
                                    <label className="text-xs text-[#9AA5B1]">Dominio asociado</label>
                                    <select value={project.domain} onChange={e => updateProject(idx, 'domain', e.target.value)} className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid={`project-domain-${idx}`}>
                                        <option value="">Seleccionar dominio</option>
                                        {domains.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="text-xs text-[#9AA5B1]">¿Por qué importa?</label>
                                    <Textarea value={project.why} onChange={e => updateProject(idx, 'why', e.target.value)} className="mt-1" rows={2} data-testid={`project-why-${idx}`} />
                                </div>
                                <div>
                                    <label className="text-xs text-[#9AA5B1]">¿Qué ha impedido el progreso?</label>
                                    <Textarea value={project.blocker} onChange={e => updateProject(idx, 'blocker', e.target.value)} className="mt-1" rows={2} data-testid={`project-blocker-${idx}`} />
                                </div>
                                <div>
                                    <label className="text-xs text-[#9AA5B1]">¿Qué pasa si sigue postergado?</label>
                                    <Textarea value={project.if_postponed} onChange={e => updateProject(idx, 'if_postponed', e.target.value)} className="mt-1" rows={2} data-testid={`project-postponed-${idx}`} />
                                </div>
                                <div>
                                    <label className="text-xs text-[#9AA5B1]">¿Qué cambia si actúa ahora?</label>
                                    <Textarea value={project.if_action} onChange={e => updateProject(idx, 'if_action', e.target.value)} className="mt-1" rows={2} data-testid={`project-action-${idx}`} />
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                    <Button className="w-full bg-[#3E5F6E] hover:bg-[#2F4A57] text-white h-12 text-base" disabled={!canGenerate} onClick={handleGenerate} data-testid="generate-plan-btn">
                        <Zap className="mr-2 h-5 w-5" /> Generar Plan SENDA 3x3
                    </Button>
                </main>
            </div>
        );
    }

    // GENERATING STEP
    if (step === 'generating') {
        return (
            <div className="min-h-screen bg-[#FBFBF9] flex items-center justify-center">
                <div className="text-center space-y-4 p-8">
                    <div className="w-20 h-20 bg-[#3E5F6E]/10 rounded-3xl flex items-center justify-center mx-auto">
                        <Loader2 className="h-10 w-10 animate-spin text-[#3E5F6E]" />
                    </div>
                    <h2 className="text-xl font-semibold text-[#1F2933] font-['Outfit']">Generando su Plan SENDA 3x3</h2>
                    <p className="text-sm text-[#616E7C] max-w-sm">Analizando sus patrones, integrando datos de los 9 módulos y construyendo su plan de ejecución personalizado...</p>
                    <Progress value={generating ? 65 : 100} className="w-64 mx-auto" />
                </div>
            </div>
        );
    }

    // REPORT STEP
    if (step === 'report' && plan) {
        const gp = plan.generated_plan || {};
        const radarData = domains.map(d => ({
            domain: d.name.split(' ')[0],
            value: plan.domain_scores?.[d.id] || 0,
            fullMark: 10
        }));
        const stateLabels = { generated: 'Generado', under_review: 'En revisión', approved: 'Aprobado', delivered: 'Entregado' };
        const stateColors = { generated: 'bg-[#E8B27A]/10 text-[#E8B27A]', under_review: 'bg-[#A29DB0]/10 text-[#A29DB0]', approved: 'bg-[#5FAF9D]/10 text-[#5FAF9D]', delivered: 'bg-[#3E5F6E]/10 text-[#3E5F6E]' };

        return (
            <div className="min-h-screen bg-[#FBFBF9]">
                <header className="bg-white border-b border-[#EAE8E3] px-4 py-3 sticky top-0 z-10">
                    <div className="max-w-4xl mx-auto flex items-center gap-3">
                        <Button variant="ghost" size="sm" onClick={() => navigate('/usuario')}><ArrowLeft className="h-4 w-4" /></Button>
                        <div className="flex-1">
                            <h1 className="text-lg font-semibold text-[#1F2933] font-['Outfit']">Plan SENDA 3x3</h1>
                            <p className="text-xs text-[#9AA5B1]">v{plan.version} | {formatDate(plan.created_at)}</p>
                        </div>
                        <Badge className={stateColors[plan.state]}>{stateLabels[plan.state]}</Badge>
                        {['approved', 'delivered'].includes(plan.state) && (
                            <Button variant="outline" size="sm" onClick={handleDownloadPDF} data-testid="download-plan-pdf"><Download className="h-4 w-4 mr-1" />PDF</Button>
                        )}
                    </div>
                </header>
                <main className="max-w-4xl mx-auto p-4 sm:p-6 space-y-6" data-testid="plan-report">
                    {/* Radar Chart */}
                    <Card className="bg-white border-[#EAE8E3]">
                        <CardContent className="p-4">
                            <p className="text-xs font-semibold text-[#425A65] uppercase tracking-wider mb-2">Mapa de Dominios (CLDF)</p>
                            <div className="h-64">
                                <ResponsiveContainer width="100%" height="100%">
                                    <RadarChart data={radarData}>
                                        <PolarGrid stroke="#EAE8E3" />
                                        <PolarAngleAxis dataKey="domain" tick={{ fontSize: 10, fill: '#616E7C' }} />
                                        <PolarRadiusAxis angle={90} domain={[0, 10]} tick={{ fontSize: 9 }} />
                                        <Radar name="Nivel" dataKey="value" stroke="#3E5F6E" fill="#3E5F6E" fillOpacity={0.2} strokeWidth={2} />
                                    </RadarChart>
                                </ResponsiveContainer>
                            </div>
                        </CardContent>
                    </Card>

                    {/* SENDA Execution Score */}
                    {executionScore && executionScore.score > 0 && (
                        <Card className="bg-white border-[#EAE8E3]" data-testid="execution-score-card">
                            <CardContent className="p-4 space-y-4">
                                <div className="flex items-center justify-between">
                                    <p className="text-xs font-semibold text-[#3E5F6E] uppercase tracking-wider">SENDA Execution Score</p>
                                    <Badge className={`text-xs ${executionScore.trend === 'up' ? 'bg-[#5FAF9D]/10 text-[#5FAF9D]' : executionScore.trend === 'down' ? 'bg-[#D07F7E]/10 text-[#D07F7E]' : 'bg-[#9AA5B1]/10 text-[#9AA5B1]'}`}>
                                        {executionScore.trend === 'up' ? 'En ascenso' : executionScore.trend === 'down' ? 'En descenso' : 'Estable'}
                                    </Badge>
                                </div>
                                <div className="flex items-center gap-6">
                                    <div className="relative w-24 h-24">
                                        <svg className="w-24 h-24 transform -rotate-90" viewBox="0 0 100 100">
                                            <circle cx="50" cy="50" r="42" fill="none" stroke="#EAE8E3" strokeWidth="8" />
                                            <circle cx="50" cy="50" r="42" fill="none" stroke="#3E5F6E" strokeWidth="8" strokeDasharray={`${executionScore.score * 2.64} 264`} strokeLinecap="round" />
                                        </svg>
                                        <div className="absolute inset-0 flex items-center justify-center">
                                            <span className="text-2xl font-bold text-[#1F2933] font-['Outfit']">{executionScore.score}</span>
                                        </div>
                                    </div>
                                    <div className="flex-1 space-y-1.5">
                                        {Object.entries(executionScore.components || {}).map(([key, val]) => {
                                            const labels = { action_completion: 'Acciones', consistency: 'Consistencia', avoidance_reduction: 'Reducción evitación', emotional_awareness: 'Conciencia emocional', behavioral_adjustment: 'Ajuste conductual' };
                                            const maxes = { action_completion: 40, consistency: 20, avoidance_reduction: 15, emotional_awareness: 10, behavioral_adjustment: 15 };
                                            return (
                                                <div key={key} className="flex items-center gap-2">
                                                    <span className="text-[10px] text-[#9AA5B1] w-28 truncate">{labels[key]}</span>
                                                    <div className="flex-1 h-1.5 bg-[#EAE8E3] rounded-full overflow-hidden">
                                                        <div className="h-full bg-[#3E5F6E] rounded-full transition-all" style={{ width: `${(val / maxes[key]) * 100}%` }} />
                                                    </div>
                                                    <span className="text-[10px] text-[#616E7C] w-8 text-right">{val}/{maxes[key]}</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                                <p className="text-xs text-[#616E7C] italic leading-relaxed">{executionScore.interpretation}</p>
                                <div className="flex items-center justify-between">
                                    <p className="text-[10px] text-[#9AA5B1]">Semanas registradas: {executionScore.weeks_tracked}</p>
                                    {scoreHistory.length > 0 && (
                                        <Button variant="ghost" size="sm" className="text-xs text-[#3E5F6E] hover:text-[#2F4A57] h-7 px-2" onClick={() => setShowEvolution(!showEvolution)} data-testid="toggle-evolution-btn">
                                            <Activity className="h-3 w-3 mr-1" />{showEvolution ? 'Ocultar evolución' : 'Ver evolución'}
                                        </Button>
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* EVOLUTION VIEW */}
                    {showEvolution && scoreHistory.length > 0 && (
                        <Card className="bg-gradient-to-br from-white to-[#F8FAFB] border-[#EAE8E3] overflow-hidden" data-testid="evolution-view">
                            <CardContent className="p-0">
                                <div className="p-5 pb-3">
                                    <div className="flex items-center gap-3 mb-1">
                                        <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-[#3E5F6E] to-[#5FAF9D] flex items-center justify-center">
                                            <TrendingUp className="h-4 w-4 text-white" />
                                        </div>
                                        <div>
                                            <h3 className="text-sm font-semibold text-[#1F2933] font-['Outfit']">Evolución de Ejecución</h3>
                                            <p className="text-[10px] text-[#9AA5B1]">Su trayectoria de conversión intención a acción</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="h-48 px-2">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <AreaChart data={scoreHistory.slice().reverse().map((s, i) => ({ week: `S${i + 1}`, score: s.score }))}>
                                            <defs>
                                                <linearGradient id="scoreGradient" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="5%" stopColor="#3E5F6E" stopOpacity={0.3} />
                                                    <stop offset="50%" stopColor="#5FAF9D" stopOpacity={0.15} />
                                                    <stop offset="95%" stopColor="#5FAF9D" stopOpacity={0} />
                                                </linearGradient>
                                            </defs>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#EAE8E3" vertical={false} />
                                            <XAxis dataKey="week" tick={{ fontSize: 10, fill: '#9AA5B1' }} axisLine={false} tickLine={false} />
                                            <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: '#9AA5B1' }} axisLine={false} tickLine={false} width={30} />
                                            <Tooltip content={({ active, payload }) => {
                                                if (active && payload && payload.length) {
                                                    const score = payload[0].value;
                                                    const zone = score >= 75 ? 'Ejecución alta' : score >= 50 ? 'En progreso' : score >= 25 ? 'Inicio de ruta' : 'Activación';
                                                    return (
                                                        <div className="bg-white/95 backdrop-blur-sm rounded-xl p-3 shadow-lg border border-[#EAE8E3]">
                                                            <p className="text-lg font-bold text-[#1F2933] font-['Outfit']">{score}<span className="text-xs text-[#9AA5B1] ml-0.5">/100</span></p>
                                                            <p className="text-[10px] text-[#5FAF9D] font-medium">{zone}</p>
                                                        </div>
                                                    );
                                                }
                                                return null;
                                            }} />
                                            <ReferenceLine y={75} stroke="#5FAF9D" strokeDasharray="4 4" strokeOpacity={0.5} />
                                            <ReferenceLine y={50} stroke="#E8B27A" strokeDasharray="4 4" strokeOpacity={0.4} />
                                            <Area type="monotone" dataKey="score" stroke="#3E5F6E" strokeWidth={2.5} fill="url(#scoreGradient)" dot={{ r: 4, fill: '#3E5F6E', stroke: '#fff', strokeWidth: 2 }} activeDot={{ r: 6, fill: '#5FAF9D', stroke: '#fff', strokeWidth: 2 }} />
                                        </AreaChart>
                                    </ResponsiveContainer>
                                </div>
                                <div className="px-5 pt-2 pb-1">
                                    <div className="flex items-center justify-center gap-4 text-[9px]">
                                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#5FAF9D]" />75+ Ejecución alta</span>
                                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#E8B27A]" />50+ En progreso</span>
                                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#9AA5B1]" />Activación</span>
                                    </div>
                                </div>
                                <div className="p-5 pt-3 space-y-3">
                                    {executionScore && (
                                        <div className="flex items-center gap-3 p-3 rounded-xl bg-gradient-to-r from-[#3E5F6E]/5 to-transparent border border-[#3E5F6E]/10">
                                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
                                                executionScore.score >= 75 ? 'bg-[#5FAF9D]/15' : executionScore.score >= 50 ? 'bg-[#E8B27A]/15' : 'bg-[#3E5F6E]/10'
                                            }`}>
                                                {executionScore.score >= 75 ? <Award className="h-5 w-5 text-[#5FAF9D]" /> : executionScore.score >= 50 ? <TrendingUp className="h-5 w-5 text-[#E8B27A]" /> : <Activity className="h-5 w-5 text-[#3E5F6E]" />}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-xs font-medium text-[#1F2933]">
                                                    {executionScore.score >= 75 ? 'Zona de ejecución sostenida' : executionScore.score >= 50 ? 'Construyendo momentum' : 'Fase de activación'}
                                                </p>
                                                <p className="text-[10px] text-[#616E7C] leading-relaxed mt-0.5">
                                                    {executionScore.score >= 75
                                                        ? 'Usted está convirtiendo intención en acción de forma consistente. El sistema está funcionando.'
                                                        : executionScore.score >= 50
                                                            ? 'La tendencia es positiva. Cada semana de registro fortalece su capacidad de ejecución.'
                                                            : 'Cada registro semanal construye su perfil. La consistencia importa más que la perfección.'}
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                    {scoreHistory.length >= 2 && (() => {
                                        const delta = (scoreHistory[0]?.score || 0) - (scoreHistory[1]?.score || 0);
                                        if (delta === 0) return null;
                                        return (
                                            <div className="flex items-center gap-2 px-1">
                                                <div className={`text-xs font-semibold ${delta > 0 ? 'text-[#5FAF9D]' : 'text-[#D07F7E]'}`}>
                                                    {delta > 0 ? '+' : ''}{delta} pts
                                                </div>
                                                <p className="text-[10px] text-[#9AA5B1]">vs semana anterior</p>
                                            </div>
                                        );
                                    })()}
                                    {scoreHistory.length > 0 && (
                                        <div className="flex items-center gap-2 px-1">
                                            <div className="flex -space-x-0.5">
                                                {scoreHistory.slice(0, 8).reverse().map((s, i) => (
                                                    <div key={i} className={`w-4 h-4 rounded-full border-2 border-white ${
                                                        s.score >= 75 ? 'bg-[#5FAF9D]' : s.score >= 50 ? 'bg-[#E8B27A]' : s.score >= 25 ? 'bg-[#9AA5B1]' : 'bg-[#EAE8E3]'
                                                    }`} title={`${s.score}/100`} />
                                                ))}
                                            </div>
                                            <p className="text-[10px] text-[#9AA5B1]">{scoreHistory.length} semana{scoreHistory.length !== 1 ? 's' : ''} de datos</p>
                                        </div>
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* Version Comparison */}
                    {versionComparison && (
                        <Card className="bg-[#E8B27A]/5 border-[#E8B27A]/20">
                            <CardContent className="p-4 space-y-2">
                                <p className="text-xs font-semibold text-[#E8B27A] uppercase">Comparación con versión anterior</p>
                                {versionComparison.priority_changes?.map((c, i) => <p key={`pc-${i}`} className="text-xs text-[#323F4B]">{c}</p>)}
                                {versionComparison.consistency_patterns?.map((c, i) => <p key={`cp-${i}`} className="text-xs text-[#5FAF9D]">{c}</p>)}
                                {versionComparison.avoidance_patterns?.map((c, i) => <p key={`ap-${i}`} className="text-xs text-[#D07F7E]">{c}</p>)}
                                {versionComparison.execution_gaps?.map((c, i) => <p key={`eg-${i}`} className="text-xs text-[#616E7C]">{c}</p>)}
                            </CardContent>
                        </Card>
                    )}

                    {/* Procrastination Analysis */}
                    {plan.procrastination_analysis && (
                        <Card className="bg-white border-[#EAE8E3]">
                            <CardContent className="p-4 space-y-2">
                                <p className="text-xs font-semibold text-[#A29DB0] uppercase">Perfil de Procrastinación</p>
                                {Object.entries(plan.procrastination_analysis).map(([key, val]) => (
                                    <div key={key} className="flex items-start gap-2 text-sm">
                                        <span className="text-xs font-medium text-[#616E7C] min-w-[140px]">{key.replace(/_/g, ' ')}:</span>
                                        <span className="text-[#1F2933]">{val}</span>
                                    </div>
                                ))}
                            </CardContent>
                        </Card>
                    )}

                    {/* Strategic Diagnosis */}
                    {gp.diagnostico_estrategico && (
                        <Card className="bg-white border-[#EAE8E3]">
                            <CardContent className="p-4 space-y-3">
                                <p className="text-xs font-semibold text-[#3E5F6E] uppercase tracking-wider">A. Diagnóstico Estratégico</p>
                                {gp.diagnostico_estrategico.problema_real && (
                                    <div className="p-3 bg-[#3E5F6E]/5 rounded-lg border-l-4 border-[#3E5F6E]">
                                        <p className="text-sm font-medium text-[#1F2933] italic">"{gp.diagnostico_estrategico.problema_real}"</p>
                                    </div>
                                )}
                                {['desequilibrio_dominios', 'patron_procrastinacion', 'alineacion_proyectos'].map(k => gp.diagnostico_estrategico[k] && (
                                    <div key={k}><p className="text-xs font-medium text-[#9AA5B1]">{k.replace(/_/g, ' ')}</p><p className="text-sm text-[#323F4B]">{gp.diagnostico_estrategico[k]}</p></div>
                                ))}
                            </CardContent>
                        </Card>
                    )}

                    {/* Block Map */}
                    {gp.mapa_bloqueo && (
                        <Card className="bg-white border-[#EAE8E3]">
                            <CardContent className="p-4 space-y-3">
                                <p className="text-xs font-semibold text-[#D07F7E] uppercase tracking-wider">B. Mapa de Bloqueo</p>
                                {Object.entries(gp.mapa_bloqueo).map(([k, v]) => (
                                    <div key={k}><p className="text-xs font-medium text-[#9AA5B1]">{k.replace(/_/g, ' ')}</p><p className="text-sm text-[#323F4B]">{v}</p></div>
                                ))}
                            </CardContent>
                        </Card>
                    )}

                    {/* Execution Plan 3x3 */}
                    {gp.plan_ejecucion && (
                        <Card className="bg-white border-[#EAE8E3]">
                            <CardContent className="p-4 space-y-5">
                                <p className="text-xs font-semibold text-[#5FAF9D] uppercase">C. Plan de Ejecución 3x3</p>
                                {gp.plan_ejecucion.map((proj, i) => (
                                    <div key={i} className="p-4 bg-[#FBFBF9] rounded-xl border border-[#EAE8E3] space-y-3">
                                        <div className="flex items-center gap-2">
                                            <span className="w-6 h-6 rounded-full bg-[#5FAF9D] text-white text-xs flex items-center justify-center font-bold">{i+1}</span>
                                            <p className="text-sm font-semibold text-[#1F2933]">{proj.proyecto}</p>
                                        </div>
                                        <p className="text-xs text-[#616E7C]"><strong>Objetivo:</strong> {proj.objetivo_reformulado}</p>
                                        <p className="text-xs text-[#616E7C]"><strong>Bloqueo:</strong> {proj.factor_bloqueo_real}</p>
                                        <div className="grid gap-2 mt-2">
                                            <div className="flex items-start gap-2 p-2 bg-white rounded-lg border border-[#EAE8E3]">
                                                <Badge className="bg-[#E8B27A]/10 text-[#E8B27A] text-[10px] shrink-0">24h</Badge>
                                                <p className="text-xs text-[#323F4B]">{proj.accion_24h}</p>
                                            </div>
                                            <div className="flex items-start gap-2 p-2 bg-white rounded-lg border border-[#EAE8E3]">
                                                <Badge className="bg-[#A29DB0]/10 text-[#A29DB0] text-[10px] shrink-0">72h</Badge>
                                                <p className="text-xs text-[#323F4B]">{proj.accion_72h}</p>
                                            </div>
                                            <div className="flex items-start gap-2 p-2 bg-white rounded-lg border border-[#EAE8E3]">
                                                <Badge className="bg-[#3E5F6E]/10 text-[#3E5F6E] text-[10px] shrink-0">7d</Badge>
                                                <p className="text-xs text-[#323F4B]">{proj.accion_7dias}</p>
                                            </div>
                                        </div>
                                        <p className="text-xs text-[#9AA5B1]"><Star className="inline h-3 w-3 mr-1" />Indicador: {proj.indicador_progreso}</p>
                                        <p className="text-xs text-[#9AA5B1]">Estrategia SENDA: {proj.estrategia_desbloqueo}</p>
                                    </div>
                                ))}
                            </CardContent>
                        </Card>
                    )}

                    {/* Daily Protocol */}
                    {gp.protocolo_diario && (
                        <Card className="bg-white border-[#EAE8E3]">
                            <CardContent className="p-4 space-y-3">
                                <p className="text-xs font-semibold text-[#425A65] uppercase tracking-wider">D. Protocolo Diario SENDA</p>
                                {[['S', 'SER', 'ser'], ['E', 'ENFOCAR', 'enfocar'], ['N', 'NOMBRAR', 'nombrar'], ['D', 'DESBLOQUEAR', 'desbloquear'], ['A', 'ACTUAR', 'actuar']].map(([letter, label, key]) => (
                                    <div key={key} className="flex items-start gap-3">
                                        <span className="w-8 h-8 rounded-lg bg-[#3E5F6E] text-white text-xs font-bold flex items-center justify-center shrink-0">{letter}</span>
                                        <div><p className="text-xs font-medium text-[#425A65]">{label}</p><p className="text-sm text-[#323F4B]">{gp.protocolo_diario[key]}</p></div>
                                    </div>
                                ))}
                            </CardContent>
                        </Card>
                    )}

                    {/* Personal Rule */}
                    {gp.regla_ejecucion_personal && (
                        <Card className="bg-gradient-to-br from-[#3E5F6E] to-[#2F4A57] border-0">
                            <CardContent className="p-6 text-center">
                                <p className="text-xs font-semibold text-white/60 uppercase mb-3">E. Regla Personal de Ejecución</p>
                                <p className="text-lg font-semibold text-white font-['Outfit'] italic">"{gp.regla_ejecucion_personal}"</p>
                            </CardContent>
                        </Card>
                    )}

                    {/* Mentor Comments */}
                    {plan.mentor_comments && (
                        <Card className="bg-[#A29DB0]/5 border-[#A29DB0]/15">
                            <CardContent className="p-4">
                                <p className="text-xs font-semibold text-[#A29DB0] uppercase mb-2">Comentarios de su mentora</p>
                                <p className="text-sm text-[#323F4B]">{plan.mentor_comments}</p>
                            </CardContent>
                        </Card>
                    )}

                    {/* Actions */}
                    <div className="flex gap-3">
                        {plan.state === 'generated' && (
                            <Button className="flex-1 bg-[#3E5F6E] hover:bg-[#2F4A57] text-white" onClick={handleSubmitForReview} data-testid="submit-review-btn">
                                <Send className="mr-2 h-4 w-4" /> Enviar a revisión
                            </Button>
                        )}
                        <Button variant="outline" onClick={handleRegenerate} data-testid="regenerate-btn">
                            <RefreshCw className="mr-2 h-4 w-4" /> Regenerar
                        </Button>
                    </div>

                    {/* Weekly Tracking */}
                    {['approved', 'delivered'].includes(plan.state) && (
                        <Card className="bg-white border-[#EAE8E3]" data-testid="weekly-checkin">
                            <CardContent className="p-4 space-y-4">
                                <div className="flex items-center gap-2">
                                    <Calendar className="h-4 w-4 text-[#5FAF9D]" />
                                    <p className="text-xs font-semibold text-[#425A65] uppercase">Seguimiento Semanal</p>
                                </div>
                                <div className="space-y-3">
                                    <div><label className="text-xs text-[#9AA5B1]">¿Qué completó esta semana?</label><Textarea value={checkinForm.completed} onChange={e => setCheckinForm(p => ({...p, completed: e.target.value}))} rows={2} className="mt-1" /></div>
                                    <div><label className="text-xs text-[#9AA5B1]">¿Qué evitó?</label><Textarea value={checkinForm.avoided} onChange={e => setCheckinForm(p => ({...p, avoided: e.target.value}))} rows={2} className="mt-1" /></div>
                                    <div><label className="text-xs text-[#9AA5B1]">¿Qué emoción apareció?</label><Input value={checkinForm.emotion} onChange={e => setCheckinForm(p => ({...p, emotion: e.target.value}))} className="mt-1" /></div>
                                    <div><label className="text-xs text-[#9AA5B1]">¿Qué aprendió sobre su patrón?</label><Textarea value={checkinForm.pattern_learning} onChange={e => setCheckinForm(p => ({...p, pattern_learning: e.target.value}))} rows={2} className="mt-1" /></div>
                                    <div><label className="text-xs text-[#9AA5B1]">¿Qué hará diferente la próxima semana?</label><Textarea value={checkinForm.next_week_action} onChange={e => setCheckinForm(p => ({...p, next_week_action: e.target.value}))} rows={2} className="mt-1" /></div>
                                    <Button className="w-full bg-[#5FAF9D] hover:bg-[#4A9A89] text-white" onClick={handleCheckin} disabled={!checkinForm.completed.trim()} data-testid="submit-checkin-btn">Registrar seguimiento</Button>
                                </div>
                                {checkins.length > 0 && (
                                    <div className="mt-4 space-y-3">
                                        <p className="text-xs font-medium text-[#616E7C]">Historial ({checkins.length})</p>
                                        {checkins.slice(0, 3).map((c, i) => (
                                            <div key={i} className="p-3 bg-[#FBFBF9] rounded-lg text-xs space-y-1">
                                                <p className="text-[#9AA5B1]">{formatDate(c.created_at)}</p>
                                                <p className="text-[#323F4B]"><strong>Completado:</strong> {c.completed}</p>
                                                <p className="text-[#323F4B]"><strong>Evitado:</strong> {c.avoided}</p>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    )}
                </main>
                <SENDAFooter />
            </div>
        );
    }

    return null;
}
